// ============================================================================
// Role-Based Menu Filter Utility
// Feature: 099-app-review-optimization
// Created: 2026-01-11
// Description: Utilities for filtering and organizing menus based on user roles
// ============================================================================

import { UserRoleValue } from '@shared/types/user';
// `menuItems` (the full tree) is still needed for breadcrumb LABEL lookup of the page the
// user is already on — labeling, not access; access goes through getMenuItemsForRole.
import { MenuItemConfig, MenuSection, menuSections, menuItems, getMenuItemsForRole } from '../../components/Navigation/MenuConfig';
import type { TenantLicenses } from '@shared/access/licenses';

// Alias for backward compatibility
type UserRole = UserRoleValue;

export interface FilteredMenu {
  sections: FilteredMenuSection[];
  flatItems: MenuItemConfig[];
  quickAccessItems: MenuItemConfig[];
}

export interface FilteredMenuSection {
  id: MenuSection;
  label: string;
  items: MenuItemConfig[];
  order: number;
}

export interface MenuSearchResult {
  item: MenuItemConfig;
  matchType: 'label' | 'description' | 'path';
  score: number;
}

// ============================================================================
// Role Hierarchy
// ============================================================================

const roleHierarchy: Record<UserRole, number> = {
  // Spec 285: enforced platform-administration role, above Admin (ADR 0091).
  SuperAdmin: 6,
  Admin: 5,
  Manager: 4,
  Operator: 3,
  Viewer: 1,
};

/**
 * Check if a role has access level equal to or higher than required role
 */
export function hasRoleAccess(userRole: UserRole, requiredRole: UserRole): boolean {
  return roleHierarchy[userRole] >= roleHierarchy[requiredRole];
}

/**
 * Get minimum required role for a menu item
 */
export function getMinimumRole(item: MenuItemConfig): UserRole {
  const roles = item.roles;
  let minRole: UserRole = 'Admin';
  let minLevel = 5;

  roles.forEach((role) => {
    const level = roleHierarchy[role];
    if (level < minLevel) {
      minLevel = level;
      minRole = role;
    }
  });

  return minRole;
}

// ============================================================================
// Menu Filtering
// ============================================================================

/**
 * Filter menu items for a principal (role + the viewing tenant's licenses).
 * Spec 285: this used to be a SECOND copy of MenuConfig's role filter; it now DELEGATES to
 * the one filter, so global search and breadcrumbs can never surface an item the sidebar
 * (and requireNavAccess) would refuse — visibility == reachability by construction.
 */
export function filterMenuByRole(role: UserRole, licenses: TenantLicenses): MenuItemConfig[] {
  return getMenuItemsForRole(role, licenses);
}

/**
 * Get complete filtered menu with sections
 */
export function getFilteredMenu(role: UserRole, licenses: TenantLicenses): FilteredMenu {
  const filteredItems = filterMenuByRole(role, licenses);
  
  const sections: FilteredMenuSection[] = menuSections
    .map((section) => ({
      id: section.id,
      label: section.label,
      order: section.order,
      items: filteredItems
        .filter((item) => item.section === section.id)
        .sort((a, b) => a.order - b.order),
    }))
    .filter((section) => section.items.length > 0);

  // Get quick access items (first item from each main section)
  const quickAccessItems = filteredItems
    .filter((item) => item.section === 'dashboards' || item.order === 0)
    .slice(0, 5);

  return {
    sections,
    flatItems: filteredItems,
    quickAccessItems,
  };
}

// ============================================================================
// Menu Search
// ============================================================================

/**
 * Search menu items by query
 */
export function searchMenuItems(
  query: string,
  role: UserRole,
  licenses: TenantLicenses,
  maxResults: number = 10
): MenuSearchResult[] {
  const normalizedQuery = query.toLowerCase().trim();
  if (!normalizedQuery) return [];

  const filteredItems = filterMenuByRole(role, licenses);
  const results: MenuSearchResult[] = [];

  const searchItem = (item: MenuItemConfig) => {
    let matchType: 'label' | 'description' | 'path' | null = null;
    let score = 0;

    // Check label match
    const labelLower = item.label.toLowerCase();
    if (labelLower === normalizedQuery) {
      matchType = 'label';
      score = 100;
    } else if (labelLower.startsWith(normalizedQuery)) {
      matchType = 'label';
      score = 80;
    } else if (labelLower.includes(normalizedQuery)) {
      matchType = 'label';
      score = 60;
    }

    // Check description match (lower priority)
    if (!matchType && item.description) {
      const descLower = item.description.toLowerCase();
      if (descLower.includes(normalizedQuery)) {
        matchType = 'description';
        score = 40;
      }
    }

    // Check path match (lowest priority)
    if (!matchType) {
      const pathLower = item.path.toLowerCase();
      if (pathLower.includes(normalizedQuery)) {
        matchType = 'path';
        score = 20;
      }
    }

    if (matchType) {
      results.push({ item, matchType, score });
    }

    // Search children
    if (item.children) {
      item.children.forEach(searchItem);
    }
  };

  filteredItems.forEach(searchItem);

  return results
    .sort((a, b) => b.score - a.score)
    .slice(0, maxResults);
}

// ============================================================================
// Quick Actions
// ============================================================================

export interface QuickAction {
  id: string;
  label: string;
  description: string;
  path: string;
  icon: string;
  shortcut?: string;
}

/**
 * Get quick actions available for a role
 */
export function getQuickActions(role: UserRole): QuickAction[] {
  const actions: QuickAction[] = [];

  if (hasRoleAccess(role, 'Admin')) {
    actions.push(
      {
        id: 'view-alerts',
        label: 'View Alerts',
        description: 'Check active system alerts',
        path: '/alerts',
        icon: 'bell',
        shortcut: 'Ctrl+Shift+L',
      }
    );
  }

  if (hasRoleAccess(role, 'Operator')) {
    actions.push(
      {
        id: 'system-health',
        label: 'System Health',
        description: 'View system health status',
        path: '/health',
        icon: 'heart-pulse',
        shortcut: 'Ctrl+Shift+H',
      },
      {
        id: 'performance',
        label: 'Performance',
        description: 'Check performance metrics',
        path: '/settings/performance',
        icon: 'chart-line',
        shortcut: 'Ctrl+Shift+P',
      }
    );
  }

  if (hasRoleAccess(role, 'Manager')) {
    actions.push({
      id: 'capacities',
      label: 'Capacities',
      description: 'Monitor capacity utilization',
      path: '/capacities',
      icon: 'server',
    });
  }

  // Actions available to all users
  actions.push(
    {
      id: 'workspaces',
      label: 'Workspaces',
      description: 'View your workspaces',
      path: '/workspaces',
      icon: 'folder',
    },
    {
      id: 'documentation',
      label: 'Documentation',
      description: 'Access system documentation',
      path: '/docs',
      icon: 'book',
      shortcut: 'Ctrl+Shift+D',
    }
  );

  return actions;
}

// ============================================================================
// Breadcrumb Generation
// ============================================================================

export interface Breadcrumb {
  label: string;
  path: string;
  isActive: boolean;
}

/**
 * Generate breadcrumbs for a given path
 */
export function generateBreadcrumbs(path: string, role: UserRole): Breadcrumb[] {
  const breadcrumbs: Breadcrumb[] = [
    { label: 'Home', path: '/', isActive: false },
  ];

  const segments = path.split('/').filter(Boolean);
  let currentPath = '';

  segments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    
    // Try to find matching menu item
    const findItem = (items: MenuItemConfig[]): MenuItemConfig | undefined => {
      for (const item of items) {
        if (item.path === currentPath) return item;
        if (item.children) {
          const found = findItem(item.children);
          if (found) return found;
        }
      }
      return undefined;
    };

    const menuItem = findItem(menuItems);
    const isActive = index === segments.length - 1;

    breadcrumbs.push({
      label: menuItem?.label || segment.charAt(0).toUpperCase() + segment.slice(1),
      path: currentPath,
      isActive,
    });
  });

  return breadcrumbs;
}

// ============================================================================
// Recent Navigation Tracking
// ============================================================================

const RECENT_NAV_KEY = 'fcp_recent_navigation';
const MAX_RECENT_ITEMS = 10;

export interface RecentNavItem {
  path: string;
  label: string;
  timestamp: number;
}

/**
 * Get recent navigation items from local storage
 */
export function getRecentNavigation(): RecentNavItem[] {
  try {
    const stored = localStorage.getItem(RECENT_NAV_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

/**
 * Add a path to recent navigation
 */
export function addToRecentNavigation(path: string, label: string): void {
  try {
    const recent = getRecentNavigation().filter((item) => item.path !== path);
    recent.unshift({ path, label, timestamp: Date.now() });
    localStorage.setItem(
      RECENT_NAV_KEY,
      JSON.stringify(recent.slice(0, MAX_RECENT_ITEMS))
    );
  } catch {
    // Ignore storage errors
  }
}

/**
 * Clear recent navigation
 */
export function clearRecentNavigation(): void {
  try {
    localStorage.removeItem(RECENT_NAV_KEY);
  } catch {
    // Ignore storage errors
  }
}

export default {
  filterMenuByRole,
  getFilteredMenu,
  searchMenuItems,
  getQuickActions,
  generateBreadcrumbs,
  getRecentNavigation,
  addToRecentNavigation,
  clearRecentNavigation,
  hasRoleAccess,
};
